import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { PaktLockup } from '../../components/pakt'
import { getCountryConfig } from '../../lib/country-config'
import type { Signer, Document } from '../../types'
import Step1View from './Step1View'
import Step2Selfie from './Step2Selfie'
import Step3Payment from './Step3Payment'

interface AlreadySignedData {
  alreadySigned: true
  signer: {
    nome: string
    signedAt: string
  }
}

interface SignerData {
  signer: Signer & { token?: string }
  document: {
    id: string
    nome: string
    countryCode: string
    status: string
    codigoVerificacao?: string
    mensagem?: string
    pdfUrl?: string
  }
  // API returns these directly (not nested under countryConfig)
  paymentConfig?: {
    primary: string
    amount: number
    currency: string
    identityStrength: string
    ownAccountWarning: boolean
  }
  selfieRecommended?: boolean
  selfieRequired?: boolean
  selfieConsentText?: string
  warningLevel?: string
  warningText?: string
  auditTrailText?: string
  // Legacy format (kept for compatibility)
  countryConfig?: {
    selfieRecommended: boolean
    paymentMethods: string[]
    primaryPayment: string
    nationalIdType: string
    nationalIdLabel: string
    legalBasis: string
    price: number
    currency: string
    currencySymbol: string
  }
  fields: Array<{
    id: string
    tipo: string
    page: number
    x: number
    y: number
    width: number
    height: number
    required: boolean
  }>
}

// Step labels are translated in the render below.
// São 3 etapas: o pagamento É a assinatura — o banco devolve nome, CPF/CNPJ e
// ISPB do pagador, prova de identidade mais forte que um traço no canvas. Quem
// paga o PIX já assinou e não volta pra tela nenhuma; quem conclui a assinatura
// é o servidor (ver services/auto-sign.service.ts).
const stepKeys = ['sign.step.view', 'sign.step.selfie', 'sign.step.payment']

// FINALIZING não é uma etapa do indicador — é a telinha de espera entre "PIX
// confirmado" e "servidor terminou de assinar" (normalmente 1–2 segundos).
const FINALIZING = stepKeys.length

function stepFromStatus(status: string): number {
  switch (status) {
    case 'PENDING_VIEW':
      return 0
    case 'PENDING_SELFIE':
      return 1
    case 'PENDING_PAYMENT':
      return 2
    case 'PENDING_SIGNATURE':
    case 'SIGNED':
      return FINALIZING
    default:
      return 0
  }
}

/**
 * Tela de espera entre o PIX confirmado e a assinatura concluída pelo servidor.
 * Fica consultando o link até a API responder que já assinou. Se demorar mais
 * que o normal (worker ocupado), avisa que pode fechar — a assinatura acontece
 * no servidor de qualquer jeito.
 */
function Finalizing({ token, onSigned }: { token: string; onSigned: () => void }) {
  const { t } = useTranslation()
  const [slow, setSlow] = useState(false)

  // onSigned muda de identidade a cada render do pai; guardar numa ref evita
  // que o efeito seja recriado e zere o intervalo e o timer a cada re-render.
  const onSignedRef = useRef(onSigned)
  onSignedRef.current = onSigned

  useEffect(() => {
    let alive = true
    const poll = setInterval(async () => {
      try {
        const raw = await api.get<{ alreadySigned?: boolean; signer?: { status: string } }>(`/api/sign/${token}`)
        if (!alive) return
        if (raw.alreadySigned || raw.signer?.status === 'SIGNED') {
          clearInterval(poll)
          onSignedRef.current()
        }
      } catch { /* segue tentando */ }
    }, 2500)
    const slowTimer = setTimeout(() => { if (alive) setSlow(true) }, 20000)
    return () => { alive = false; clearInterval(poll); clearTimeout(slowTimer) }
  }, [token])

  return (
    <div className="bg-white border border-pakt-line p-8 text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pakt-orange mx-auto mb-5" />
      <h2 className="text-xl font-semibold text-pakt-ink mb-2">
        {t('sign.finalizing.title', 'Payment confirmed — finishing your signature')}
      </h2>
      <p className="text-sm text-gray-500">
        {t('sign.finalizing.desc', 'Your bank has confirmed the transfer. We are recording your signature — this only takes a moment.')}
      </p>
      {slow && (
        <p className="text-sm text-gray-400 mt-4">
          {t('sign.finalizing.slow', 'Taking longer than usual. You can close this page — your signature is already secured and you will receive a copy by email.')}
        </p>
      )}
    </div>
  )
}

export default function SignerFlow() {
  const { t, i18n } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [signerData, setSignerData] = useState<SignerData | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(true)
  const [localeReady, setLocaleReady] = useState(false)
  const [error, setError] = useState('')
  const [alreadySigned, setAlreadySigned] = useState<AlreadySignedData['signer'] | null>(null)

  useEffect(() => {
    async function fetchData() {
      try {
        const raw = await api.get<SignerData | AlreadySignedData>(`/api/sign/${token}`)
        if ((raw as AlreadySignedData).alreadySigned) {
          setAlreadySigned((raw as AlreadySignedData).signer)
          return
        }
        const data = raw as SignerData
        setSignerData(data)
        setCurrentStep(stepFromStatus(data.signer.status))

        // Wait for locale change before rendering translated content
        const countryConfig =
          getCountryConfig(data.signer.countryCode) ||
          getCountryConfig(data.document.countryCode)
        if (countryConfig) {
          await i18n.changeLanguage(countryConfig.locale)

          // Apply RTL direction for Arabic/Hebrew locales
          document.documentElement.dir = countryConfig.rtl ? 'rtl' : 'ltr'
          document.documentElement.lang = countryConfig.locale
        }
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const status = (err as { status: number }).status
          if (status === 404) {
            setError(t('sign.error.invalidLink', 'This signing link is invalid or has expired.'))
          } else if (status === 410) {
            setError(t('sign.error.expired', 'This document has expired.'))
          } else {
            setError(t('sign.error.loadFailed', 'Failed to load signing session.'))
          }
        } else {
          setError(t('sign.error.loadFailed', 'Failed to load signing session.'))
        }
      } finally {
        setLocaleReady(true)
        setLoading(false)
      }
    }
    fetchData()
  }, [token])

  // O código de verificação já veio no GET inicial — leva ele pra tela final
  // pra o signatário sair dali com o link de conferência público na mão.
  function goToComplete() {
    const code = signerData?.document?.codigoVerificacao
    navigate(`/sign/${token}/complete${code ? `?code=${encodeURIComponent(code)}` : ''}`)
  }

  // Recarrega os dados do signatário sem quebrar quando ele já assinou: nesse
  // caso a API devolve { alreadySigned }, um shape sem `document` que faria a
  // tela explodir se fosse jogado direto no estado.
  function refreshSignerData() {
    if (!token) return
    api
      .get<SignerData | AlreadySignedData>(`/api/sign/${token}`)
      .then((raw) => {
        if ((raw as AlreadySignedData).alreadySigned) {
          goToComplete()
          return
        }
        setSignerData(raw as SignerData)
      })
      .catch(() => {})
  }

  function handleStepComplete() {
    if (currentStep >= FINALIZING) {
      goToComplete()
      return
    }
    const next = currentStep + 1
    setCurrentStep(next)
    // Na tela de espera quem consulta é o próprio Finalizing — recarregar aqui
    // só correria o risco de trazer o shape de "já assinado".
    if (next < FINALIZING) refreshSignerData()
  }

  function handleSkipSelfie() {
    setCurrentStep(2)
    refreshSignerData()
  }

  if (loading || !localeReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    )
  }

  if (alreadySigned) {
    const signedDate = new Date(alreadySigned.signedAt).toLocaleString(i18n.language, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white border border-pakt-line p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">✅</div>
          <h1 className="text-xl font-semibold text-pakt-ink mb-2">
            {t('sign.already.title', 'Document already signed')}
          </h1>
          <p className="text-gray-600 mb-2">
            <strong>{alreadySigned.nome}</strong> {t('sign.already.signedOn', 'signed this document on:')}
          </p>
          <p className="text-pakt-orange font-medium mb-6">{signedDate}</p>
          <p className="text-sm text-gray-500 mb-6">
            {t('sign.already.linkUsed', 'This signing link has already been used. If you need a copy of the document, please contact the sender.')}
          </p>
          <Link
            to="/"
            className="inline-block px-5 py-2 text-sm font-medium text-pakt-orange border border-pakt-line hover:bg-pakt-surface transition"
          >
            {t('sign.error.backToHome', '← Back to home')}
          </Link>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white border border-pakt-line p-8 max-w-md w-full text-center">
          <div className="font-pakt-mono text-[10px] tracking-[0.2em] text-pakt-orange uppercase mb-4">[ERR]</div>
          <h1 className="text-xl font-semibold text-pakt-ink mb-2">{t('sign.error.unableToProceed', 'Unable to proceed')}</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500 mb-6">
            {t('sign.error.contactSender', 'Please contact the sender to generate a new signing link.')}
          </p>
          <Link
            to="/"
            className="inline-block px-5 py-2 text-sm font-medium text-pakt-orange border border-pakt-line hover:bg-pakt-surface transition"
          >
            {t('sign.error.backToHome', '← Back to home')}
          </Link>
        </div>
      </div>
    )
  }

  if (!signerData) return null

  const country =
    getCountryConfig(signerData.signer.countryCode) ||
    getCountryConfig(signerData.document.countryCode)

  // Map API response to the format expected by child components
  // The API returns paymentConfig/selfieRecommended at top level,
  // but child components expect a nested countryConfig object
  const mappedSignerData = { ...signerData, signer: { ...signerData.signer, token: token ?? "" } }
  if (!mappedSignerData.countryConfig && mappedSignerData.paymentConfig) {
    mappedSignerData.countryConfig = {
      selfieRecommended: mappedSignerData.selfieRecommended ?? false,
      paymentMethods: [mappedSignerData.paymentConfig.primary],
      primaryPayment: mappedSignerData.paymentConfig.primary,
      nationalIdType: country?.nationalId?.type || '',
      nationalIdLabel: country?.nationalId?.fieldName || 'ID',
      legalBasis: mappedSignerData.auditTrailText || country?.lawName || '',
      price: mappedSignerData.paymentConfig.amount,
      currency: mappedSignerData.paymentConfig.currency,
      currencySymbol: mappedSignerData.paymentConfig.currency,
    }
  }

  return (
    <div className="min-h-screen bg-pakt-surface">
      {/* Header */}
      <header className="bg-white border-b border-pakt-line">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <PaktLockup size={20} />
              {country && <span className="text-lg">{country.flag}</span>}
            </div>
            <span className="text-sm text-gray-500 truncate max-w-[150px] sm:max-w-none">{signerData.document.nome}</span>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2">
            {stepKeys.map((key, i) => {
              const completed = i < currentStep
              const active = i === currentStep
              return (
                <div key={key} className="flex items-center flex-1">
                  <div className="flex items-center gap-2 flex-1">
                    <div
                      className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-xs sm:text-sm font-pakt-mono font-medium border-2 ${
                        completed
                          ? 'bg-pakt-orange border-pakt-orange text-white'
                          : active
                            ? 'border-pakt-ink bg-pakt-ink text-white'
                            : 'border-pakt-line bg-white text-pakt-ink-3'
                      }`}
                    >
                      {completed ? '✓' : i + 1}
                    </div>
                    <span
                      className={`text-xs font-pakt-mono tracking-wide hidden sm:inline ${
                        completed
                          ? 'text-pakt-orange'
                          : active
                            ? 'text-pakt-ink'
                            : 'text-pakt-ink-3'
                      }`}
                    >
                      {t(key)}
                    </span>
                  </div>
                  {i < stepKeys.length - 1 && (
                    <div
                      className={`h-0.5 flex-1 mx-1 sm:mx-2 hidden sm:block ${
                        i < currentStep ? 'bg-pakt-orange' : 'bg-pakt-line'
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </header>

      {/* Step Content */}
      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-8">
        {currentStep === 0 && (
          <Step1View signerData={mappedSignerData} onComplete={handleStepComplete} />
        )}
        {currentStep === 1 && (
          <Step2Selfie
            signerData={mappedSignerData}
            onComplete={handleStepComplete}
            onSkip={handleSkipSelfie}
          />
        )}
        {currentStep === 2 && (
          <Step3Payment signerData={mappedSignerData} onComplete={handleStepComplete} />
        )}
        {currentStep === FINALIZING && (
          <Finalizing token={token ?? ''} onSigned={goToComplete} />
        )}
      </main>
    </div>
  )
}
